/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect } from 'react';

import { useUiStore, type ThemePreference } from '@/stores/uiStore';

type ResolvedTheme = 'light' | 'dark';

/**
 * Browser chrome colour per theme: the GMDS surface the app header is drawn on
 * (`--color-surface`). Written out because a <meta> cannot read a CSS variable; keep in step with
 * the pre-paint script in index.html.
 */
const THEME_COLOR: Record<ResolvedTheme, string> = { light: '#ffffff', dark: '#181e28' };

function resolve(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return pref;
}

/**
 * Writes the theme the way the kit's ThemeProvider and index.html's pre-paint script do:
 * `data-theme` for the kit and this app's CSS, `data-mode` for the GM Design System tokens, plus
 * the browser chrome colour.
 */
function apply(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.dataset.mode = theme;
  document
    .querySelectorAll('meta[name="theme-color"]')
    .forEach((tag) => tag.setAttribute('content', THEME_COLOR[theme]));
}

/** Applies the current theme preference to <html data-theme data-mode> and tracks OS changes. */
export function useTheme() {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);

  useEffect(() => {
    apply(resolve(theme));
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => apply(resolve('system'));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  return { theme, setTheme, resolved: resolve(theme) };
}
